/**
 * Session Store - 2025 Best Practices
 * Centralized session management aligned with backend express-session architecture
 *
 * Backend Architecture:
 * - Sessions are auto-created by SessionGuard on every request
 * - No separate initialize endpoint - sessions are created on-the-fly
 * - verify endpoint returns: { sessionId, userId, csrfToken }
 * - userId is null for anonymous visitors (before payment)
 * - userId is set after admin login or payment
 */

import { defineStore } from 'pinia';
import { ref, computed, readonly, watch } from 'vue';
import { getApiClient } from '../../composables/useApiClient';
import { useAsyncState, useRetry } from '../utils/store-helpers';
import logger from '../../utils/logger';

/**
 * Session state interface matching backend contract
 */
export interface SessionState {
  sessionId: string | null
  csrfToken: string | null
  userId: string | null
  clientId: string
  isInitialized: boolean
  isConnected: boolean
}

/**
 * Verify session response type
 */
type VerifySessionData = {
  sessionId: string
  userId: string | null
  csrfToken: string
}

/**
 * Session data stored in localStorage
 */
interface StoredSessionData {
  sessionId: string | null
  csrfToken: string | null
  userId: string | null
}

/**
 * Load session data from localStorage
 */
function loadSessionFromStorage(): StoredSessionData {
  try {
    const storedData = localStorage.getItem('ulrich-session')
    if (storedData) {
      const parsed = JSON.parse(storedData)
      return {
        sessionId: parsed.sessionId || null,
        csrfToken: parsed.csrfToken || null,
        userId: parsed.userId || null,
      }
    }
  } catch (error) {
    logger.error('Failed to load session from localStorage', { error })
  }
  return { sessionId: null, csrfToken: null, userId: null }
}

/**
 * Save session data to localStorage
 */
function saveSessionToStorage(data: StoredSessionData): void {
  try {
    localStorage.setItem('ulrich-session', JSON.stringify(data))
  } catch (error) {
    logger.error('Failed to save session to localStorage', { error })
  }
}

/**
 * Clear session data from localStorage
 */
function clearSessionFromStorage(): void {
  try {
    localStorage.removeItem('ulrich-session')
  } catch (error) {
    logger.error('Failed to clear session from localStorage', { error })
  }
}

export const useSessionStore = defineStore('session', () => {
  // === STATE ===
  // Load initial values from localStorage
  const stored = loadSessionFromStorage()
  const sessionId = ref<string | null>(stored.sessionId)
  const csrfToken = ref<string | null>(stored.csrfToken)
  const userId = ref<string | null>(stored.userId)
  const clientId = ref<string>('frontend_client')
  const isInitialized = ref<boolean>(false)
  const isConnected = ref<boolean>(false)

  // === ASYNC STATE MANAGEMENT ===
  const {
    loading: isLoading,
    error: lastError,
    reset: resetAsyncState
  } = useAsyncState()

  const { execute: executeWithRetry, retryCount } = useRetry<boolean>(3, 1000)

  // === AUTOMATIC LOCALSTORAGE SYNC ===
  // Automatically sync session data to localStorage whenever it changes
  // This ensures consistency between store state and HttpClient.getSessionHeaders()
  watch(
    [sessionId, csrfToken, userId],
    ([newSessionId, newCsrfToken, newUserId]) => {
      if (newSessionId && newCsrfToken) {
        // Save valid session data
        saveSessionToStorage({
          sessionId: newSessionId,
          csrfToken: newCsrfToken,
          userId: newUserId,
        })
      } else if (!newSessionId && !newCsrfToken) {
        // Clear localStorage if session is cleared
        clearSessionFromStorage()
      }
    },
    { deep: false }
  )

  // === LISTEN TO SESSION REFRESH EVENTS ===
  // React to session refreshes from API client (e.g., after CSRF 403 error)
  if (typeof window !== 'undefined') {
    // Listen for session refresh from API client
    window.addEventListener('session-refreshed', ((event: CustomEvent) => {
      const { sessionId: newSessionId, csrfToken: newCsrfToken, userId: newUserId } = event.detail
      if (newSessionId && newCsrfToken) {
        sessionId.value = newSessionId
        csrfToken.value = newCsrfToken
        userId.value = newUserId
        isInitialized.value = true
        isConnected.value = true
        logger.sessionEvent('✅ Session updated after API client refresh', {
          sessionId: newSessionId.substring(0, 8) + '...'
        })
      }
    }) as EventListener)

    // Listen for session clear
    window.addEventListener('session-cleared', (() => {
      clearSession()
      logger.sessionEvent('Session cleared by API client')
    }) as EventListener)
  }

  // === API CLIENT ===
  // Use the centralized API client from composable
  // Note: CSRF token auto-refresh is registered globally in useApiClient.ts
  const apiClient = getApiClient();

  // === COMPUTED ===
  const isValid = computed(() => {
    return !!(sessionId.value && csrfToken.value)
  })

  const isAuthenticated = computed(() => {
    return !!userId.value
  })

  const sessionInfo = computed<SessionState>(() => ({
    sessionId: sessionId.value,
    csrfToken: csrfToken.value,
    userId: userId.value,
    clientId: clientId.value,
    isInitialized: isInitialized.value,
    isConnected: isConnected.value
  }))

  const connectionStatus = computed(() => {
    if (isLoading.value) return 'connecting'
    if (!isInitialized.value) return 'initializing'
    if (!isConnected.value) return 'disconnected'
    if (!isValid.value) return 'expired'
    return 'connected'
  })

  // === ACTIONS ===
  /**
   * Set session data from backend verify response
   * localStorage sync happens automatically via watch()
   */
  const setSession = (data: VerifySessionData): void => {
    sessionId.value = data.sessionId
    csrfToken.value = data.csrfToken
    userId.value = data.userId

    isInitialized.value = true
    isConnected.value = true
    resetAsyncState()

    logger.sessionEvent('Session updated', {
      sessionId: sessionId.value?.substring(0, 8) + '...',
      hasCSRF: !!csrfToken.value,
      isAuthenticated: isAuthenticated.value,
      isValid: isValid.value
    })
  }

  /**
   * Get session headers for API requests
   * Includes session ID and CSRF token
   */
  const getSessionHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {}

    if (sessionId.value && csrfToken.value) {
      headers['X-Session-Token'] = sessionId.value
      headers['X-CSRF-Token'] = csrfToken.value
    }

    return headers
  }

  /**
   * Get auth headers with client credentials
   * Used for authenticated requests
   */
  const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Client-ID': clientId.value,
      'X-Client-Secret': 'frontend_secret_key_2024',
      'Origin': typeof window !== 'undefined' ? window.location.origin : ''
    }

    if (sessionId.value) {
      headers['X-Session-Token'] = sessionId.value
    }

    if (csrfToken.value) {
      headers['X-CSRF-Token'] = csrfToken.value
    }

    return headers
  }

  /**
   * Clear session state
   * localStorage sync happens automatically via watch()
   */
  const clearSession = (): void => {
    sessionId.value = null
    csrfToken.value = null
    userId.value = null
    isInitialized.value = false
    isConnected.value = false
    resetAsyncState()

    logger.sessionEvent('Session cleared')
  }

  /**
   * Initialize session by calling verify endpoint
   * Backend auto-creates session via SessionGuard if needed
   */
  const initializeSession = async (): Promise<boolean> => {
    logger.sessionEvent('Initializing session', {
      hasExistingSession: !!sessionId.value,
      isInitialized: isInitialized.value,
      isValid: isValid.value
    })

    // Check if we already have a valid session
    if (isInitialized.value && isValid.value) {
      logger.sessionEvent('Using existing valid session')
      return true
    }

    // Try to restore from localStorage (already loaded on store creation)
    // If session data exists in refs, it was loaded from localStorage
    if (sessionId.value && csrfToken.value) {
      isInitialized.value = true
      isConnected.value = true

      logger.sessionEvent('Using restored session from localStorage', {
        sessionId: sessionId.value?.substring(0, 8) + '...',
        isAuthenticated: isAuthenticated.value
      })
      return true
    }

    // Verify/create session with backend (SessionGuard auto-creates if needed)
    return executeWithRetry(async () => {
      logger.sessionEvent('Verifying session with backend')
      const response = await apiClient.session.verify()

      logger.sessionEvent('Session verified successfully', {
        sessionId: response.data.sessionId?.substring(0, 8) + '...',
        userId: response.data.userId?.substring(0, 8) + '...' || 'anonymous'
      })

      setSession({
        sessionId: response.data.sessionId,
        csrfToken: response.data.csrfToken,
        userId: response.data.userId
      })

      return true
    }).catch((error) => {
      logger.error('Session initialization failed', { error })
      return false
    })
  }

  /**
   * Verify current session with backend
   * Returns true if session is valid
   */
  const verifySession = async (): Promise<boolean> => {
    if (!sessionId.value) return false

    try {
      const response = await apiClient.session.verify()

      if (response.data.sessionId) {
        // Update userId if it changed (e.g., after payment)
        // localStorage sync happens automatically via watch()
        if (response.data.userId !== userId.value) {
          userId.value = response.data.userId
        }
        isConnected.value = true
        return true
      } else {
        isConnected.value = false
        return false
      }
    } catch (error) {
      logger.error('Session verification failed', { error })
      isConnected.value = false
      return false
    }
  }

  // Set initialization flags if we loaded valid session data from localStorage
  if (sessionId.value && csrfToken.value) {
    isInitialized.value = true
    isConnected.value = true
    logger.sessionEvent('Store initialized with persisted session')
  }

  return {
    // State (readonly)
    sessionId: readonly(sessionId),
    csrfToken: readonly(csrfToken),
    userId: readonly(userId),
    clientId: readonly(clientId),
    isInitialized: readonly(isInitialized),
    isConnected: readonly(isConnected),
    isLoading: readonly(isLoading),
    lastError: readonly(lastError),
    retryCount: readonly(retryCount),

    // Computed
    isValid,
    isAuthenticated,
    sessionInfo,
    connectionStatus,

    // Actions
    setSession,
    clearSession,
    getSessionHeaders,
    getAuthHeaders,
    initializeSession,
    verifySession
  }
})
